import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { partidasAPI } from '../services/api';
import socketService from '../services/socket';
import { parDelCampo, parPersonal } from '../utils/handicap';
import './WaitingRoom.css';

function WaitingRoom() {
  const navigate = useNavigate();
  const { codigo } = useParams();
  const [partida, setPartida] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);
  const [copied, setCopied] = useState(false);

  const jugadorId = localStorage.getItem('jugadorId');
  const esCreador = partida?.jugadores?.find(j => j.id === Number(jugadorId))?.es_creador;

  useEffect(() => {
    loadPartida();
    joinSocket();

    // Escuchar eventos de Socket.IO
    socketService.on('jugador-unido', handleJugadorUnido);
    socketService.on('partida-iniciada', handlePartidaIniciada);

    return () => {
      socketService.off('jugador-unido', handleJugadorUnido);
      socketService.off('partida-iniciada', handlePartidaIniciada);
    };
  }, [codigo]);

  const loadPartida = async () => {
    try {
      const response = await partidasAPI.getByCode(codigo);
      setPartida(response.data);

      // Si la partida ya empezó, redirigir al juego
      if (response.data.estado === 'en_curso') {
        navigate(`/juego/${codigo}`);
      } else if (response.data.estado === 'finalizada') {
        navigate(`/resultados/${codigo}`);
      }
    } catch (err) {
      console.error('Error al cargar partida:', err);
      setError('No se pudo cargar la partida. Verifica el código.');
    } finally {
      setLoading(false);
    }
  };

  const joinSocket = () => {
    socketService.joinPartida(codigo);
  };

  const handleJugadorUnido = (data) => {
    console.log('Nuevo jugador unido:', data);
    loadPartida(); // Recargar datos de la partida
  };

  const handlePartidaIniciada = () => {
    navigate(`/juego/${codigo}`);
  };

  const handleStartGame = async () => {
    if (!esCreador) return;

    setStarting(true);
    setError('');

    try {
      await partidasAPI.start(codigo);
      socketService.iniciarPartida(codigo);
      navigate(`/juego/${codigo}`);
    } catch (err) {
      console.error('Error al iniciar partida:', err);
      setError(err.response?.data?.error || 'Error al iniciar la partida');
      setStarting(false);
    }
  };

  const shareLink = () => {
    const url = `${window.location.origin}/unirse/${codigo}`;

    if (navigator.share) {
      navigator.share({
        title: 'Unirse a partida de golf',
        text: `Únete a mi partida de golf! Código: ${codigo}`,
        url: url
      }).catch(err => console.log('Error al compartir:', err));
    } else {
      // Fallback: copiar al portapapeles
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      });
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(codigo).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#2e7d32' }}>Cargando partida...</p>
      </div>
    );
  }

  if (error && !partida) {
    return (
      <div className="container" style={{ paddingTop: '60px' }}>
        <div className="error">{error}</div>
        <button className="btn btn-secondary btn-full" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    );
  }

  const parCampo = parDelCampo(partida?.hoyos);

  return (
    <div className="waiting-room">
      <div className="header">
        <h1>Sala de Espera</h1>
        <p>{partida?.campo_nombre} · Par {parCampo}</p>
      </div>

      <div className="container">
        {error && <div className="error">{error}</div>}

        {copied && <div className="success">¡Copiado al portapapeles!</div>}

        <div className="card fade-in">
          <div className="code-display">
            <div className="code-label">Código de partida</div>
            <div className="code-value" onClick={copyCode}>
              {codigo}
            </div>
            <div className="code-hint">Toca para copiar</div>
          </div>

          <button className="btn btn-secondary btn-full share-btn" onClick={shareLink}>
            📤 Compartir enlace
          </button>
        </div>

        <div className="card players-card">
          <h2>Jugadores ({partida?.jugadores?.length || 0}/5)</h2>

          <div className="players-list">
            {partida?.jugadores?.map((jugador, index) => (
              <div key={jugador.id} className="player-item">
                <div className="player-number">{index + 1}</div>
                <div className="player-info">
                  <div className="player-name">
                    {jugador.nombre}
                    {Boolean(jugador.es_creador) && <span className="creator-badge">Creador</span>}
                  </div>
                  <div className="player-handicap">
                    Handicap: {jugador.handicap} · Su par: {parPersonal(parCampo, jugador.handicap)}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {partida?.jugadores?.length < 5 && (
            <div className="waiting-message">
              <p>Esperando más jugadores...</p>
              <small>Comparte el código para que otros se unan</small>
            </div>
          )}
        </div>

        {esCreador && (
          <button
            className="btn btn-primary btn-full start-btn"
            onClick={handleStartGame}
            disabled={starting || !partida?.jugadores?.length}
          >
            {starting ? 'Iniciando...' : '🏌️ Iniciar Partida'}
          </button>
        )}

        {!esCreador && (
          <div className="waiting-creator">
            <p>⏳ Esperando a que el creador inicie la partida...</p>
          </div>
        )}

        <button
          className="btn btn-secondary btn-full"
          onClick={() => navigate('/')}
          style={{ marginTop: '12px' }}
        >
          Salir
        </button>
      </div>
    </div>
  );
}

export default WaitingRoom;
